/**
 * Quality / Compliance assessments (vfs-service GET /quality/assessments...): GDPR, NIS2, DORA and the EU AI Act, one at
 * a time. For each: the score and counts, whether it shows in the overview and counts toward the release gate (editors
 * switch both: opt-in, per project), the checks that have not run yet (Run them), and its controls, filtered by status
 * (AssessmentControl: checks with evidence, questions to answer, Fix with AI). The disclaimer always shows: evidence for
 * an assessment, never a certification. The report downloads as JSON or opens as a printable page.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DownloadSimple, Play, Printer } from '@phosphor-icons/react';
import {
  assessmentReport, getAssessment, getAssessments, saveAssessmentAnswers, saveAssessmentSettings,
} from '../../services/QualityService';
import { Pill, ScoreRing, State, SubHead } from './QualityCommon';
import AssessmentControl from './AssessmentControl';
import {
  ASSESSMENT_AREA, CONTROL_STATUS, STATUS_ORDER, assessmentFixPrompt,
} from './assessmentModel';
import { checkTitle, errorText, plural } from './qualityModel';

/** Opens a printable page: the window opens now, while still a click; the HTML comes through a blob URL. */
async function openPrintable(projectId, framework) {
  const win = window.open('', '_blank');
  try {
    const html = await assessmentReport(projectId, framework, 'html');
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    if (win) { win.location.href = url; } else { window.open(url, '_blank'); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    win?.close?.();
    throw e;
  }
}

function Settings({ assessment, settings, canEdit, onSave, busy }) {
  const enabled = settings.enabled.includes(assessment.id);
  const inGate = settings.gate.includes(assessment.id);
  const toggle = (list, on) => (on ? [...new Set([...list, assessment.id])] : list.filter((id) => id !== assessment.id));
  return (
    <div className="quality-assess-settings quality-small" data-testid="assess-settings">
      <label>
        <input checked={enabled} disabled={!canEdit || busy} onChange={(e) => onSave({ enabled: toggle(settings.enabled, e.target.checked), gate: e.target.checked ? settings.gate : toggle(settings.gate, false) })} type="checkbox" />
        {' '}Show in the Quality overview
      </label>
      <label>
        <input checked={inGate} disabled={!canEdit || busy} onChange={(e) => onSave({ gate: toggle(settings.gate, e.target.checked) })} type="checkbox" />
        {' '}Block Publish while a blocker control has a gap
      </label>
      {inGate && (
        <label>
          <input checked={settings.blockUnanswered} disabled={!canEdit || busy} onChange={(e) => onSave({ blockUnanswered: e.target.checked })} type="checkbox" />
          {' '}Also while a blocker control is not answered yet
        </label>
      )}
      {!canEdit && <span className="quality-muted">Editors and the owner change these.</span>}
    </div>
  );
}

Settings.propTypes = { assessment: PropTypes.object.isRequired, busy: PropTypes.bool, canEdit: PropTypes.bool, onSave: PropTypes.func.isRequired, settings: PropTypes.object.isRequired };

export default function QualityAssessments({
  projectId, framework = null, canEdit = false, onBack, onOpenCheck, onOpenProvenance, onOpenCode = null, onOpenView = null, onAskAI = null, onRun = null, onChanged = () => {}, runNote = null,
}) {
  const [list, setList] = useState(null);
  const [current, setCurrent] = useState(framework);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [filter, setFilter] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const answer = await getAssessments(projectId);
      setList(answer);
      setError(null);
      setCurrent((c) => c || answer.frameworks?.find((f) => f.enabled)?.id || answer.frameworks?.[0]?.id || null);
    } catch (e) { setError(e); }
  }, [projectId]);
  const loadDetail = useCallback(async (id) => {
    if (!id) { return; }
    try { setDetail(await getAssessment(projectId, id)); } catch (e) { setNotice(`Could not load the assessment: ${errorText(e)}`); }
  }, [projectId]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setFilter(null); loadDetail(current); }, [current, loadDetail]);

  const editable = Boolean(list?.canEdit ?? canEdit);
  const settings = list?.settings || { enabled: [], gate: [], blockUnanswered: false };
  const saveSettings = async (patch) => {
    setBusy(true);
    setNotice(null);
    try {
      await saveAssessmentSettings(projectId, patch);
      await loadList();
      await loadDetail(current);
      onChanged();
    } catch (e) { setNotice(`Could not save: ${errorText(e)}`); } finally { setBusy(false); }
  };
  const saveAnswers = async (answers) => {
    const next = await saveAssessmentAnswers(projectId, current, answers);
    setDetail(next);
    loadList();
    onChanged();
  };
  const report = async (format) => {
    setNotice(null);
    try {
      if (format === 'html') { await openPrintable(projectId, current); } else { await assessmentReport(projectId, current, 'json'); }
    } catch (e) { setNotice(`Could not get the report: ${errorText(e)}`); }
  };
  /** What an evidence link opens; null when there is nowhere to go from here. */
  const openEvidence = (e) => {
    if (e.kind === 'code' && e.path && onOpenCode) { return () => onOpenCode({ path: e.path, line: e.line || null }); }
    if (e.kind === 'data' && onOpenView) { return () => onOpenView({ tab: 'data', focus: { store: e.store, object: e.object } }); }
    if (e.kind === 'restore' && onOpenView) { return () => onOpenView({ tab: 'data', focus: { view: 'restore' } }); }
    if (e.kind === 'traces' && onOpenView) { return () => onOpenView({ tab: 'traces', focus: e.trace || e.traceId ? { trace: e.trace || e.traceId } : null }); }
    if (e.kind === 'provenance') { return () => onOpenProvenance(e.id); }
    if (e.kind === 'check' && e.check) { return () => onOpenCheck(e.check); }
    return null;
  };
  const answerApplies = async (value) => {
    try { await saveAnswers([{ question: '_applies', value }]); } catch (e) { setNotice(errorText(e)); }
  };

  const shown = (detail?.controls || [])
    .filter((c) => !filter || c.status === filter)
    .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
  const countOf = (status) => (detail?.controls || []).filter((c) => !status || c.status === status).length;

  return (
    <div className="quality-sub" data-testid="quality-assessments">
      <SubHead onBack={onBack} title={ASSESSMENT_AREA.title}>
        <span className="quality-grow" />
        <button className="quality-btn" disabled={!detail} onClick={() => report('json')} type="button"><DownloadSimple aria-hidden size={14} />Download report (JSON)</button>
        <button className="quality-btn" disabled={!detail} onClick={() => report('html')} type="button"><Printer aria-hidden size={14} />Open printable report</button>
      </SubHead>
      <p className="quality-muted">{ASSESSMENT_AREA.purpose}</p>
      <p className="quality-banner is-info" data-testid="assess-disclaimer" role="note">{list?.disclaimer || 'Evidence for an assessment, not a certification.'}</p>
      {notice && <p className="quality-banner is-warn" role="alert">{notice}</p>}
      {error && !list && <State action={<button className="quality-btn" onClick={loadList} type="button">Try again</button>} title="Could not load the assessments" tone="bad">{errorText(error)}</State>}
      {!error && !list && <div className="quality-state" role="status">Loading the assessments…</div>}

      {list && (
        <div aria-label="Assessment" className="quality-chips" role="group">
          <span className="quality-cap">Assessment</span>
          {list.frameworks.map((f) => (
            <button aria-pressed={f.id === current} className="quality-chip" data-testid={`assess-pick-${f.id}`} key={f.id} onClick={() => setCurrent(f.id)} type="button">
              {f.name}{f.enabled ? '' : ' (off)'}{f.totals?.gap ? ` · ${plural(f.totals.gap, 'gap')}` : ''}
            </button>
          ))}
        </div>
      )}

      {detail && detail.id === current && (
        <>
          <section aria-labelledby="assess-title" className="quality-card quality-pad quality-assess-head">
            <div className="quality-prov-line">
              <ScoreRing score={detail.score} />
              <div className="quality-grow">
                <h2 id="assess-title">{detail.name} <span className="quality-muted quality-small">{detail.version}</span></h2>
                <p className="quality-small">{detail.about}</p>
                <p className="quality-small" data-testid="assess-totals">
                  {detail.totals.evidenced} evidenced · {detail.totals.partial} partly · <b className={detail.totals.gap ? 'quality-ink-fail' : undefined}>{plural(detail.totals.gap, 'gap')}</b> · {detail.totals.todo} to do · {detail.totals.na} not applicable · {detail.totals.answered} of {plural(detail.totals.questions, 'question')} answered
                </p>
                {detail.gateRule && <Pill tone={detail.gateRule.status === 'fail' ? 'fail' : 'pass'}>{detail.gateRule.status === 'fail' ? 'Blocks Publish' : 'In the release gate'}</Pill>}
              </div>
            </div>
            <Settings assessment={detail} busy={busy} canEdit={editable} onSave={saveSettings} settings={settings} />
          </section>

          {!detail.applies && (
            <div className="quality-banner is-info" data-testid="assess-not-applicable" role="status">
              <span>Not applicable: {detail.appliesReason}</span>
              {editable && detail.id === 'aiAct' && (
                <span className="quality-banner-actions"><button className="quality-btn is-sm" onClick={() => answerApplies('yes')} type="button">It uses AI</button></span>
              )}
            </div>
          )}
          {detail.applies && detail.appliesBy && editable && (
            <p className="quality-muted quality-small">{detail.appliesReason} <button className="quality-link" onClick={() => answerApplies(null)} type="button">Let Flow decide again</button></p>
          )}

          {detail.applies && detail.checksNotRun?.length > 0 && (
            <div className="quality-banner is-warn" data-testid="assess-not-run" role="status">
              <span>No evidence yet from {detail.checksNotRun.map(checkTitle).join(', ')}: these checks have not run.</span>
              {editable && onRun && (
                <span className="quality-banner-actions"><button className="quality-btn is-sm" onClick={() => onRun({ checks: detail.checksNotRun })} type="button"><Play aria-hidden size={12} />Run them</button></span>
              )}
            </div>
          )}
          {runNote}

          <div aria-label="Status" className="quality-chips" role="group">
            <span className="quality-cap">Status</span>
            <button aria-pressed={!filter} className="quality-chip" onClick={() => setFilter(null)} type="button">All ({countOf(null)})</button>
            {STATUS_ORDER.filter((s) => countOf(s)).map((s) => (
              <button aria-pressed={filter === s} className="quality-chip" key={s} onClick={() => setFilter(s)} type="button">{CONTROL_STATUS[s].label} ({countOf(s)})</button>
            ))}
          </div>
          <ul aria-label="Controls" className="quality-assess-controls">
            {shown.map((control) => (
              <AssessmentControl
                canEdit={editable && detail.applies}
                control={control}
                key={`${detail.id}-${control.id}`}
                onFix={onAskAI ? (c) => onAskAI(assessmentFixPrompt(detail, c)) : null}
                onOpenEvidence={openEvidence}
                onSaveAnswers={saveAnswers}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

QualityAssessments.propTypes = {
  canEdit: PropTypes.bool,
  // The assessment to open on (gdpr | nis2 | dora | aiAct); default: the first turned on.
  framework: PropTypes.string,
  // (prompt) => void: the project chat ("Fix with AI").
  onAskAI: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  // () => void: after a settings change or answers (the overview and the gate follow).
  onChanged: PropTypes.func,
  onOpenCheck: PropTypes.func.isRequired,
  // ({ path, line }) => void: the code at a line.
  // null: the person's plan hides Code, the evidence's file is named without a link
  onOpenCode: PropTypes.func,
  onOpenProvenance: PropTypes.func.isRequired,
  // ({ tab, focus }) => void: another workspace view (Data on a store, Restore points, Traces).
  onOpenView: PropTypes.func,
  // ({ checks }) => void: runs these Quality checks.
  onRun: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  // Why the checks did not start when "Run them" was pressed (QualityPanel RunNote), or null.
  runNote: PropTypes.node,
};
